import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompanyStatusEnum } from "@/constants/enums/companyStatus.enum";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { FedexPackagingEnum } from "@/constants/enums/fedexPackaging.enum";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { IdentityDocumentRequiredEnum } from "@/constants/enums/identityDocumentRequired.enum";
import { ImportLicenseRequiredEnum } from "@/constants/enums/importLicenseRequired.enum";
import { InvoicePaymentStatusEnum } from "@/constants/enums/invoicePaymentStatus.enum";
import { ManualTrackingEnum } from "@/constants/enums/manualTracking.enum";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { ParcelProductTypeEnum } from "@/constants/enums/parcelProductType.enum";
import { PaymentMethodEnum } from "@/constants/enums/paymentMethod.enum";
import { PickupTimeCotationEnum } from "@/constants/enums/pickupTimeCotation.enum";
import { RegionCustomEnum } from "@/constants/enums/regionCustom.enum";
import { RolesEnum } from "@/constants/enums/roles.enum";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { PickupTime } from "@/types/pickupDay";

/**
 * Libellés traduits des enums métier (namespace `enums`).
 *
 * Chaque hook renvoie un `Record<valeur, libellé>` dont l'ordre des clés est l'ordre d'affichage
 * (utilisé tel quel par les hooks d'options, cf. `useEnumOptions.ts`).
 */

const toLabels = <T extends string>(
  values: readonly T[],
  getLabel: (value: T) => string,
): Record<T, string> =>
  Object.fromEntries(values.map((value) => [value, getLabel(value)])) as Record<T, string>;

const ROLES_ORDER = [
  RolesEnum.SUPERADMIN,
  RolesEnum.ADMIN,
  RolesEnum.MEMBER,
  RolesEnum.ACCOUNTANT,
  RolesEnum.DRIVER,
  RolesEnum.PACKER,
] as const;

export const useRoleLabels = (): Record<RolesEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(ROLES_ORDER, (value) => t(`roles.${value}`)), [t]);
};

const COMPANY_STATUS_ORDER = [
  CompanyStatusEnum.PENDING,
  CompanyStatusEnum.ACTIVE,
  CompanyStatusEnum.BLOCKED,
] as const;

export const useCompanyStatusLabels = (): Record<CompanyStatusEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(COMPANY_STATUS_ORDER, (value) => t(`companyStatus.${value}`)), [t]);
};

const CUSTOM_QUOTATION_TYPE_ORDER = [
  CustomQuotationTypeEnum.PALETTE,
  CustomQuotationTypeEnum.PARCEL,
  CustomQuotationTypeEnum.OTHER,
] as const;

export const useCustomQuotationTypeLabels = (): Record<CustomQuotationTypeEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(CUSTOM_QUOTATION_TYPE_ORDER, (value) => t(`customQuotationType.${value}`)),
    [t],
  );
};

const DELIVERY_STATUS_ORDER = [
  DeliveryStatusEnum.WAITING,
  DeliveryStatusEnum.ONGOING,
  DeliveryStatusEnum.BLOCKED,
  DeliveryStatusEnum.DELIVERED,
  DeliveryStatusEnum.PARTIALLY_DELIVERED,
  DeliveryStatusEnum.TOTALLY_DELIVERED,
  DeliveryStatusEnum.FAILED,
] as const;

export const useDeliveryStatusLabels = (): Record<DeliveryStatusEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(DELIVERY_STATUS_ORDER, (value) => t(`deliveryStatus.${value}`)),
    [t],
  );
};

const EXPEDITION_BLOCKED_REASON_ORDER = [
  ExpeditionBlockedReasonEnum.INCORRECT_ADDRESS,
  ExpeditionBlockedReasonEnum.AUTO,
  ExpeditionBlockedReasonEnum.DAMAGED,
  ExpeditionBlockedReasonEnum.LOST_PACKAGE,
  ExpeditionBlockedReasonEnum.DELAY,
  ExpeditionBlockedReasonEnum.LABEL_PRINTED_TIME_EXCEEDED,
  ExpeditionBlockedReasonEnum.CLEARANCE_SCAN_TIME_EXCEEDED,
  ExpeditionBlockedReasonEnum.LAST_SCAN_TIME_EXCEEDED,
  ExpeditionBlockedReasonEnum.CUSTOMS_CLEARANCE_IN_PROGRESS,
  ExpeditionBlockedReasonEnum.RECIPIENT_ABSENT,
  ExpeditionBlockedReasonEnum.FEDEX,
  ExpeditionBlockedReasonEnum.UNKNOWN,
  ExpeditionBlockedReasonEnum.PARTIAL_DELIVERY,
  ExpeditionBlockedReasonEnum.RELAY_POINT,
  ExpeditionBlockedReasonEnum.CUSTOMS_ISSUE,
  ExpeditionBlockedReasonEnum.PNC_ISSUE,
  ExpeditionBlockedReasonEnum.RETURNED,
  ExpeditionBlockedReasonEnum.TAX_NON_PAYMENT,
] as const;

export const useExpeditionBlockedReasonLabels = (): Record<ExpeditionBlockedReasonEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      toLabels(EXPEDITION_BLOCKED_REASON_ORDER, (value) => t(`expeditionBlockedReason.${value}`)),
    [t],
  );
};

const EXPEDITION_TYPE_ORDER = [
  ExpeditionTypeEnum.QUOTATION,
  ExpeditionTypeEnum.CUSTOM_QUOTATION,
] as const;

export const useExpeditionTypeLabels = (): Record<ExpeditionTypeEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(EXPEDITION_TYPE_ORDER, (value) => t(`expeditionType.${value}`)),
    [t],
  );
};

const IDENTITY_DOCUMENT_REQUIRED_ORDER = [
  IdentityDocumentRequiredEnum.NEVER,
  IdentityDocumentRequiredEnum.ALWAYS,
  IdentityDocumentRequiredEnum.IF_NO_LICENSE,
] as const;

export const useIdentityDocumentRequiredLabels = (): Record<
  IdentityDocumentRequiredEnum,
  string
> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      toLabels(IDENTITY_DOCUMENT_REQUIRED_ORDER, (value) => t(`identityDocumentRequired.${value}`)),
    [t],
  );
};

const IMPORT_LICENSE_REQUIRED_ORDER = [
  ImportLicenseRequiredEnum.NEVER,
  ImportLicenseRequiredEnum.ALWAYS,
  ImportLicenseRequiredEnum.ON_RESTRICTION_EXCEEDED,
] as const;

export const useImportLicenseRequiredLabels = (): Record<ImportLicenseRequiredEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(IMPORT_LICENSE_REQUIRED_ORDER, (value) => t(`importLicenseRequired.${value}`)),
    [t],
  );
};

const MANUAL_TRACKING_ORDER = [ManualTrackingEnum.MANUAL, ManualTrackingEnum.AUTOMATIC] as const;

export const useManualTrackingLabels = (): Record<ManualTrackingEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(MANUAL_TRACKING_ORDER, (value) => t(`manualTracking.${value}`)),
    [t],
  );
};

const OTHER_CUSTOM_QUOTATION_TYPE_ORDER = [
  OtherCustomQuotationTypeEnum.SOUS_DOUANE,
  OtherCustomQuotationTypeEnum.T1_T2,
  OtherCustomQuotationTypeEnum.RFSE,
  OtherCustomQuotationTypeEnum.DEFAULT,
  OtherCustomQuotationTypeEnum.DAE,
  OtherCustomQuotationTypeEnum.RP_FISC_PART,
  OtherCustomQuotationTypeEnum.RP_FISC_PRO,
] as const;

/** Libellés courts (badges). */
export const useOtherCustomQuotationTypeLabels = (): Record<
  OtherCustomQuotationTypeEnum,
  string
> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      toLabels(OTHER_CUSTOM_QUOTATION_TYPE_ORDER, (value) =>
        t(`otherCustomQuotationType.${value}`),
      ),
    [t],
  );
};

const OTHER_CUSTOM_QUOTATION_TYPE_OPTION_ORDER = [
  OtherCustomQuotationTypeEnum.DEFAULT,
  OtherCustomQuotationTypeEnum.DAE,
  OtherCustomQuotationTypeEnum.SOUS_DOUANE,
  OtherCustomQuotationTypeEnum.RFSE,
  OtherCustomQuotationTypeEnum.RP_FISC_PART,
  OtherCustomQuotationTypeEnum.RP_FISC_PRO,
  OtherCustomQuotationTypeEnum.T1_T2,
] as const;

/** Libellés longs (sélecteurs, détail). */
export const useOtherCustomQuotationTypeLongLabels = (): Record<
  OtherCustomQuotationTypeEnum,
  string
> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () =>
      toLabels(OTHER_CUSTOM_QUOTATION_TYPE_OPTION_ORDER, (value) =>
        t(`otherCustomQuotationTypeOption.${value}`),
      ),
    [t],
  );
};

const PACKAGING_FILTER_ORDER = [
  PackagingFilterEnum.STANDARD,
  PackagingFilterEnum.WOODBOX,
  PackagingFilterEnum.CUSTOM_SINGLE,
  PackagingFilterEnum.CUSTOM_MULTIPLE,
  PackagingFilterEnum.CUSTOM_PALETTE,
  PackagingFilterEnum.CUSTOM_PARCEL,
] as const;

export const usePackagingFilterLabels = (): Record<PackagingFilterEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(PACKAGING_FILTER_ORDER, (value) => t(`packagingFilter.${value}`)),
    [t],
  );
};

const PARCEL_PRODUCT_TYPE_ORDER = [
  ParcelProductTypeEnum.BOTTLE,
  ParcelProductTypeEnum.OTHER,
] as const;

export const useParcelProductTypeLabels = (): Record<ParcelProductTypeEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(PARCEL_PRODUCT_TYPE_ORDER, (value) => t(`parcelProductType.${value}`)),
    [t],
  );
};

const PAYMENT_METHOD_ORDER = [PaymentMethodEnum.VIREMENT, PaymentMethodEnum.PRELEVEMENT] as const;

export const usePaymentMethodLabels = (): Record<PaymentMethodEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(PAYMENT_METHOD_ORDER, (value) => t(`paymentMethod.${value}`)), [t]);
};

export const usePaymentMethodShortLabels = (): Record<PaymentMethodEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(PAYMENT_METHOD_ORDER, (value) => t(`paymentMethodShort.${value}`)),
    [t],
  );
};

const PICKUP_TIME_COTATION_ORDER = [
  PickupTimeCotationEnum.MORNING,
  PickupTimeCotationEnum.AFTERNOON,
] as const;

export const usePickupTimeCotationLabels = (): Record<PickupTimeCotationEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(PICKUP_TIME_COTATION_ORDER, (value) => t(`pickupTimeCotation.${value}`)),
    [t],
  );
};

/** Libellé court en minuscules ("matin"), chaîne vide si la valeur n'est pas un créneau connu. */
export const usePickupSlotShortLabel = () => {
  const { t } = useTranslation("enums");
  return (value: string | null | undefined): string =>
    value && value in PickupTimeCotationEnum
      ? t(`pickupSlotShort.${value as PickupTimeCotationEnum}`)
      : "";
};

const PICKUP_TIME_ORDER = ["MORNING", "AFTERNOON", "ALLDAY"] as const satisfies PickupTime[];

export const usePickupTimeLabels = (): Record<PickupTime, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(PICKUP_TIME_ORDER, (value) => t(`pickupTime.${value}`)), [t]);
};

const REGION_CUSTOM_ORDER = [
  RegionCustomEnum.AFRICA,
  RegionCustomEnum.NORTHAMERICA,
  RegionCustomEnum.SOUTHAMERICA,
  RegionCustomEnum.ASIA,
  RegionCustomEnum.DOMTOM,
  RegionCustomEnum.EUROPE,
  RegionCustomEnum.OCEANIA,
  RegionCustomEnum.EUROPEANUNION,
  RegionCustomEnum.OTHER,
] as const;

export const useRegionCustomLabels = (): Record<RegionCustomEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(REGION_CUSTOM_ORDER, (value) => t(`regionCustom.${value}`)), [t]);
};

const WINE_COLOR_ORDER = [
  WineColorEnum.ROUGE,
  WineColorEnum.BLANC,
  WineColorEnum.ROSE,
  WineColorEnum.ROSEEFFERVESCENT,
  WineColorEnum.BLANCEFFERVESCENT,
  WineColorEnum.CHAMPAGNE,
  WineColorEnum.AMBER,
] as const;

export const useWineColorLabels = (): Record<WineColorEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(() => toLabels(WINE_COLOR_ORDER, (value) => t(`wineColor.${value}`)), [t]);
};

export const useFileTypeLabels = (): Record<FileTypeEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(Object.values(FileTypeEnum), (value) => t(`fileType.${value}`)),
    [t],
  );
};

export const useFedexPackagingLabels = (): Record<FedexPackagingEnum, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(Object.values(FedexPackagingEnum), (value) => t(`fedexPackaging.${value}`)),
    [t],
  );
};

/** Statuts de paiement affichables (FAILED n'a pas de libellé). */
export type DisplayedInvoicePaymentStatus = Exclude<
  InvoicePaymentStatusEnum,
  InvoicePaymentStatusEnum.FAILED
>;

const INVOICE_PAYMENT_STATUS_ORDER = [
  InvoicePaymentStatusEnum.TOBEPAID,
  InvoicePaymentStatusEnum.TOBECONFIRMED,
  InvoicePaymentStatusEnum.PROCESSING,
  InvoicePaymentStatusEnum.PAID,
] as const satisfies DisplayedInvoicePaymentStatus[];

export const useInvoicePaymentStatusLabels = (): Record<DisplayedInvoicePaymentStatus, string> => {
  const { t } = useTranslation("enums");
  return useMemo(
    () => toLabels(INVOICE_PAYMENT_STATUS_ORDER, (value) => t(`invoicePaymentStatus.${value}`)),
    [t],
  );
};
